import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type {
	SubscriptionOffer,
	SubscriptionOfferPatch,
	SubscriptionTriggerPatch,
} from "../../lib/types";
import { useCampaignMutation } from "./useCampaignMutation";

export const useSubscriptionOffers = (campaignId: string | undefined) =>
	useQuery({
		queryKey: ["subscription-offers", campaignId],
		queryFn: () =>
			api.get<{ items: SubscriptionOffer[] }>(`/api/campaigns/${campaignId}/subscriptions`),
		enabled: Boolean(campaignId),
	});

const useOfferMutation = <TVariables>(
	campaignId: string,
	run: (variables: TVariables) => Promise<unknown>,
) => useCampaignMutation(campaignId, [["subscription-offers", campaignId]], run);

export const useCreateSubscriptionOffer = (campaignId: string) =>
	useOfferMutation(campaignId, (patch: SubscriptionOfferPatch) =>
		api.post(`/api/campaigns/${campaignId}/subscriptions`, patch),
	);

export const useUpdateSubscriptionOffer = (campaignId: string) =>
	useOfferMutation(
		campaignId,
		({ offerId, ...patch }: { offerId: string } & SubscriptionOfferPatch) =>
			api.put(`/api/campaigns/${campaignId}/subscriptions/${offerId}`, patch),
	);

export const useDeleteSubscriptionOffer = (campaignId: string) =>
	useOfferMutation(campaignId, (offerId: string) =>
		api.remove(`/api/campaigns/${campaignId}/subscriptions/${offerId}`),
	);

export const useAddTrigger = (campaignId: string) =>
	useOfferMutation(
		campaignId,
		({ offerId, ...patch }: { offerId: string } & SubscriptionTriggerPatch) =>
			api.post(`/api/campaigns/${campaignId}/subscriptions/${offerId}/triggers`, patch),
	);

export const useUpdateTrigger = (campaignId: string) =>
	useOfferMutation(
		campaignId,
		({
			offerId,
			triggerId,
			...patch
		}: { offerId: string; triggerId: string } & SubscriptionTriggerPatch) =>
			api.put(`/api/campaigns/${campaignId}/subscriptions/${offerId}/triggers/${triggerId}`, patch),
	);

export const useDeleteTrigger = (campaignId: string) =>
	useOfferMutation(campaignId, ({ offerId, triggerId }: { offerId: string; triggerId: string }) =>
		api.remove(`/api/campaigns/${campaignId}/subscriptions/${offerId}/triggers/${triggerId}`),
	);
